/* eslint-disable no-invalid-this, no-alert */
import {t} from "@lingui/core/macro";
import Backbone from "backbone";
import _ from "underscore";

import Button from "@khanacademy/wonder-blocks-button";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {RequireReauth} from "@khan/user-settings";

// TODO(somewhatabstract, JIRA-XXX3): Improve our redux patterns so that we
// can pass a valid ApolloGqlClient from the react components.
// eslint-disable-next-line no-restricted-imports
import {getUnsafeApolloGqlClient} from "#data-access/gql/get-unsafe-apollo-gql-client.ts";
import {saveAndUpdateProfile} from "#data-access/user-profile-provider.tsx";
import $ from "#jquery/jquery-with-bootstrap-modal.js";
// NOTE(jeresig): This should be loaded asynchronously
import hasUserSeen from "#promos-package/has-promo-been-seen.ts";
import {markAsSeen} from "#promos-package/mark-promo-as-seen.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import getUsernamePickerTemplate from "./username-picker.handlebars.ts";

const validateUsername = async function (
    profile: any,
    gqlClient: ApolloGqlClient,
    username: string,
) {
    // Can't define validate() (or I don't understand how to)
    // because of https://github.com/documentcloud/backbone/issues/233

    // Remove any feedback if user returns to their current username
    if (username === profile.get("username")) {
        profile.trigger("validate:username");
        return;
    }

    const {isUsernameAvailable} = await import("@khan/auth-password");
    isUsernameAvailable(gqlClient, username).then(
        ({result: isAvailable, reason}) => {
            profile.trigger("validate:username", reason, isAvailable);
        },
    );
};

/**
 * Code to handle the profile info changer.
 */
const UsernamePicker: any = Backbone.View.extend({
    id: "username-picker-container",
    savePending_: false,

    /**
     * Whether or not the nickname is acceptable/valid to try and save.
     */
    nicknameFieldAcceptable_: true,

    /**
     * Whether or not the username field is acceptable. Note that an empty
     * username, while invalid, is acceptable in the field if the user
     * had not selected one prior to opening this view.
     */
    usernameFieldAcceptable_: true,

    events: {
        "keypress .nickname": "onNicknameKeypress_",
        "keypress .username": "onUsernameKeypress_",
        "click :input": "onInputClick_",
    },

    delegateEvents: function (this: any, events) {
        this.$el
            .on("input", ".nickname", _.bind(this.onNicknameInput_, this))
            .on("input", ".username", _.bind(this.onUsernameInput_, this));
        UsernamePicker.__super__.delegateEvents.call(this, events);
    },

    onInputClick_: function (e) {
        // Force focus on the input after a click. This is to work around
        // the fact that in IE, the input can sometimes not get focus,
        // even though the user is properly typing in it. Since only
        // events with focus fire keyup events, we would otherwise miss them.
        $(e.target).focus();
    },

    initialize: function (this: any, params) {
        this.template = getUsernamePickerTemplate();
        this.shouldShowUsernameWarning_ = false;
        this.model.bind("validate:nickname", this.onValidateNickname_, this);
        this.model.bind("validate:username", this.onValidateUsername_, this);
    },

    remove: function (this: any) {
        this._unmountModalFooter?.();
        UsernamePicker.__super__.remove.call(this);
    },

    /**
     * Restore the form to the last saved state.
     *
     * This is called:
     *     - After the user exits the modal without saving.
     *     - After the model save fails.
     */
    restoreLastSavedState_: function (this: any) {
        // These older fields aren't edited on the fly, so we need to manually
        // copy the model values into the form elements.
        const nickname = (
            this.model as ProfileModelType | null | undefined
        )?.get("nickname");
        const username = (
            this.model as ProfileModelType | null | undefined
        )?.get("username");
        this.$(".nickname").val(nickname);
        this.$(".username").val(username);
        this.$(".example-username").text(username);
    },

    render: function (this: any) {
        // TODO: Make idempotent
        // maybe making the onHidden_ function obsolete
        const context = {
            username: (this.model as ProfileModelType | null | undefined)?.get(
                "username",
            ),
            nickname: (this.model as ProfileModelType | null | undefined)?.get(
                "nickname",
            ),
        } as const;
        const html = this.template(context);

        this.$el
            .html(html)
            .addClass("modal fade hide fixed-username")
            .modal({
                show: false, // TODO(alpert): why is this needed?
                keyboard: true,
                backdrop: true,
            })
            .bind("hidden", _.bind(this.onHidden_, this))
            .bind("shown", _.bind(this.onShown_, this));

        this._renderModalFooter(!this.usernameFieldAcceptable_, t`Save`);
        return this;
    },

    onCancelClicked_: function (this: any) {
        this.toggle();
    },

    toggle: function (this: any, model?: ProfileModelType | null) {
        this.$el.modal("toggle");

        if (model) {
            this.model = model;
            this.restoreLastSavedState_();
        }
    },

    /**
     * Idempotent function to hide the modal. Will only hide if the modal
     * isn't already hidden.
     */
    hide: function (this: any) {
        if (!this.$el.is(":hidden")) {
            this.$el.modal("hide");
        }
    },

    _renderModalFooter: function (
        this: any,
        saveButtonDisabled,
        saveButtonLabel,
    ) {
        const genericSubmitButton = (onClick: () => unknown) => (
            <Button
                onClick={onClick}
                size="small"
                style={{minWidth: 150}}
                disabled={saveButtonDisabled}
            >
                {saveButtonLabel}
            </Button>
        );

        const modalFooter = (
            <div>
                <Button
                    onClick={() => this.onCancelClicked_()}
                    size="small"
                    style={{minWidth: 120}}
                    kind="secondary"
                >
                    {t`Cancel`}
                </Button>
                <span className="modal-footer-spacer" />
                {this.getFormValue_(".username") ===
                (this.model as ProfileModelType | null | undefined)?.get(
                    "username",
                ) ? (
                    genericSubmitButton(() => this.onSaveClick_())
                ) : (
                    <RequireReauth
                        identifier={
                            (
                                this.model as
                                    | ProfileModelType
                                    | null
                                    | undefined
                            )?.get("username") ||
                            (
                                this.model as
                                    | ProfileModelType
                                    | null
                                    | undefined
                            )?.get("email")
                        }
                        onSuccess={() => this.onSaveClick_()}
                        onFailure={() => {}}
                    >
                        {(invokeReauth) => genericSubmitButton(invokeReauth)}
                    </RequireReauth>
                )}
            </div>
        );
        this._unmountModalFooter = deprecatedReactRender(
            modalFooter,
            this._getModalFooterEl(),
        );
    },

    _getModalFooterEl: function (this: any) {
        return this.$(".modal-footer")[0];
    },

    onShown_: function (this: any) {
        // Unfortunately, there is no way to discern within the onHidden_()
        // callback how the modal was dismissed (via clicking "Save", via
        // clicking "Cancel", via hitting ESC, etc.). Since we need to reset
        // the model back to the previous saved state on every non-save modal
        // dismissal, we manually track whether the "Save" button was clicked.
        // Since onShown_() is called every time the modal is shown, this is
        // an appropriate time to reset saveClicked_.
        this.saveClicked_ = false;

        // If the user already has a username, be sure that we warn them about
        // the holding period that happens if they change it.
        hasUserSeen(
            // TODO(somewhatabstract, JIRA-XXX3): Improve our redux patterns so that we
            // can pass a valid ApolloGqlClient from the react components.
            // eslint-disable-next-line no-restricted-syntax
            getUnsafeApolloGqlClient(),
            "Username change warning",
            function (this: any, hasSeen) {
                this.shouldShowUsernameWarning_ = !hasSeen;
            },
            this,
        );
    },

    onHidden_: function (this: any) {
        if (!this.saveClicked_) {
            // Revert the modal and model back to last saved state if modal
            // was closed some other way besides the "Save" button being
            // clicked
            this.restoreLastSavedState_();
        }

        this.nicknameFieldAcceptable_ = true;
        this.usernameFieldAcceptable_ = true;
        this.$(".notification").hide();
        this.$(".sidenote").text("").removeClass("success error");
        this._renderModalFooter(false, t`Save`);
    },

    // TODO(benkomalo): consolidate with new Forms utility in settings
    onNicknameInput_: function (this: any, e) {
        (this.model as ProfileModelType | null | undefined)?.validateNickname(
            this.getFormValue_(".nickname"),
        );
    },

    onNicknameKeypress_: function (this: any, e) {
        if (e.keyCode === 13) {
            // Treat enter as "tab" to the next field.
            this.$(".username").focus();
        }
    },

    onUsernameKeypress_: function (this: any, e) {
        if (e.keyCode === 13) {
            if (this.usernameFieldAcceptable_) {
                this.onSaveClick_();
            }
            validateUsername(
                this.model,
                // TODO(somewhatabstract, JIRA-XXX3): Improve our redux patterns so that we
                // can pass a valid ApolloGqlClient from the react components.
                // eslint-disable-next-line no-restricted-syntax
                getUnsafeApolloGqlClient(),
                this.getFormValue_(".username"),
            );
        }
    },

    onUsernameInput_: function (this: any, e) {
        this._renderModalFooter(true, t`Save`);
        if (
            this.shouldShowUsernameWarning_ &&
            (this.model as ProfileModelType | null | undefined)?.get("username")
        ) {
            $(".notification.error").show();
            const kaid = (
                this.model as ProfileModelType | null | undefined
            )?.get("kaid");

            if (kaid) {
                markAsSeen(
                    // TODO(somewhatabstract, JIRA-XXX3): Improve our redux patterns so that we
                    // can pass a valid ApolloGqlClient from the react components.
                    // eslint-disable-next-line no-restricted-syntax
                    getUnsafeApolloGqlClient(),
                    "Username change warning",
                    kaid,
                );
            }
            this.shouldShowUsernameWarning_ = false;
        }
        this.$(".example-username").text(this.getFormValue_(".username"));

        this.showSidenote_(".username-row", t`Checking...`);
        this.debouncedValidateUsername_();
    },

    debouncedValidateUsername_: _.debounce(function (this: any) {
        validateUsername(
            this.model,
            // TODO(somewhatabstract, JIRA-XXX3): Improve our redux patterns so that we
            // can pass a valid ApolloGqlClient from the react components.
            // eslint-disable-next-line no-restricted-syntax
            getUnsafeApolloGqlClient(),
            this.getFormValue_(".username"),
        );
    }, 1000),

    syncSaveButtonState_: function (this: any) {
        this._renderModalFooter(!this.usernameFieldAcceptable_, t`Save`);
    },

    onValidateNickname_: function (this: any, isValid: boolean) {
        if (isValid) {
            this.showSidenote_(".nickname-row", "");
        } else {
            this.showSidenote_(".nickname-row", t`Can't leave empty.`, false);
        }

        this.usernameFieldAcceptable_ = isValid;
        this.syncSaveButtonState_();
    },

    onValidateUsername_: function (
        this: any,
        message: string,
        isValid: boolean,
    ) {
        this.showSidenote_(".username-row", message, isValid);

        // Accept the username if it's unchanged or if it's changed to
        // a valid value. Note that users may start with no username, which
        // is itself an "invalid" value (empty), but it's acceptable here
        // since we don't require they select a new username.
        this.usernameFieldAcceptable_ =
            this.getFormValue_(".username") ===
                (this.model as ProfileModelType | null | undefined)?.get(
                    "username",
                ) || isValid;
        this.syncSaveButtonState_();
    },

    getFormValue_: function (this: any, selector: string) {
        return $.trim(this.$(selector).val());
    },

    /**
     * Show the message in the specified row's sidenote.
     * If isValid === true, show a green checkmark (success),
     * if isValid === false, show a red x (error),
     * otherwise, don't show any such indicator.
     */
    showSidenote_: function (
        this: any,
        rowSelector: string,
        message: string | null | undefined = "",
        isValid: boolean,
    ) {
        const jelSidenote = this.$(rowSelector).find(".sidenote");

        // Note that isValid may be undefined, in which case neither class
        // is enabled.
        jelSidenote.toggleClass("success", isValid === true);
        jelSidenote.toggleClass("error", isValid === false);
        jelSidenote.text(message);
    },

    onSaveClick_: function (this: any) {
        this.saveClicked_ = true;

        const nickname = this.getFormValue_(".nickname");
        const username = this.getFormValue_(".username");
        const bio = this.getFormValue_("#bio-picker");

        // TODO(somewhatabstract, JIRA-XXX3): Improve our redux patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        saveAndUpdateProfile(getUnsafeApolloGqlClient(), {
            nickname,
            username,
            bio,
        })
            .then(() => {
                this.onSaveSuccess_();
            })
            .catch(() => {
                this.onSaveRejected_("Failed");
            });

        this._renderModalFooter(true, t`Save`);
        const usernameChange =
            username !==
            (this.model as ProfileModelType | null | undefined)?.get(
                "username",
            );

        if (usernameChange) {
            // Keep the modal open and wait for a save success, since this
            // is an important, ocean-boiling operation.
            this._renderModalFooter(true, t`Saving...`);
        } else {
            this.hide();
        }
    },

    onSaveSuccess_: function (this: any) {
        this.hide();
    },

    onSaveRejected_: function (this: any, errorText: string) {
        window.alert(errorText);
        this.hide();

        // Since "Save" was clicked and the modal might have already been
        // closed at this point (if the username wasn't changed), we're tasked
        // with restoring the last saved state ourselves.
        this.restoreLastSavedState_();
    },
});

export default UsernamePicker;
